import { useEffect, useRef, useState } from "react";
import { Icons } from "@/components/ui/icons";
import { cn } from "@/lib/cn";

type CardState = "working" | "question" | "done";
type CardKind = "subagent" | "computer";

interface MockComputerActivity {
  appLabel: string;
  appBg: string;
  actionLabel: string;
  screenshot: boolean;
}

interface MockCard {
  id: string;
  kind: CardKind;
  title: string;
  state: CardState;
  progress: string;
  iconLabel: string;
  /** Merged computer-use: live activity cycles in the logo tile. */
  computer?: MockComputerActivity;
}

const INITIAL_CARDS: MockCard[] = [
  {
    id: "code",
    kind: "subagent",
    title: "Denker · Calendar showcase",
    state: "working",
    progress: "Building the frame…",
    iconLabel: "D",
  },
  {
    id: "spotify",
    kind: "computer",
    title: "Spotify",
    state: "working",
    progress: "Opening track…",
    iconLabel: "S",
  },
  {
    id: "mail",
    kind: "subagent",
    title: "Denker · Inbox triage",
    state: "question",
    progress: "Needs your answer",
    iconLabel: "M",
  },
  {
    id: "research",
    kind: "subagent",
    title: "Competitor research",
    state: "working",
    progress: "Launch · Spotify · Click",
    iconLabel: "D",
    computer: { appLabel: "Spotify", appBg: "bg-[#1DB954] text-black", actionLabel: "Click", screenshot: true },
  },
];

const QUESTION_OPTIONS = ["Yes, archive them", "Keep unread"];

const LIVE_CYCLE_MS = 1600;

/** Collapsed-sliver logo: cycles action → screenshot → app while working. */
function MockLiveLogo({ card }: { card: MockCard }) {
  const computer = card.computer;
  const [index, setIndex] = useState(0);
  const cycling = card.state === "working" && Boolean(computer);
  useEffect(() => {
    setIndex(0);
  }, [card.id, card.state]);
  useEffect(() => {
    if (!cycling) return;
    const timer = window.setInterval(() => setIndex((current) => (current + 1) % 3), LIVE_CYCLE_MS);
    return () => window.clearInterval(timer);
  }, [cycling]);
  if (!computer) return null;
  const frame = index % 3;
  if (frame === 0) {
    return (
      <span
        aria-hidden="true"
        title={computer.actionLabel}
        data-testid={`mock-pill-live-action-${card.id}`}
        className="grid h-8 w-8 shrink-0 place-items-center rounded-[9px] bg-white/10 text-white"
      >
        <Icons.MousePointerClick className="h-4 w-4" />
      </span>
    );
  }
  if (frame === 1 && computer.screenshot) {
    return (
      <span
        aria-hidden="true"
        title="Screenshot"
        data-testid={`mock-pill-live-screenshot-${card.id}`}
        className="grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-[9px] bg-gradient-to-br from-sky-500/60 via-white/20 to-emerald-500/50 text-white"
      >
        <Icons.Camera className="h-4 w-4" />
      </span>
    );
  }
  return (
    <span
      aria-hidden="true"
      title={computer.appLabel}
      data-testid={`mock-pill-live-app-${card.id}`}
      className={cn(
        "grid h-8 w-8 shrink-0 place-items-center rounded-[9px] text-sm font-bold",
        computer.appBg,
      )}
    >
      {computer.appLabel.slice(0, 1)}
    </span>
  );
}

/** Minimal pill: app icon + agent name + one live progress line. */
function MockPill({
  card,
  onDismiss,
}: {
  card: MockCard;
  onDismiss: () => void;
}) {
  return (
    <div
      className="group/pill relative flex w-[264px] items-center gap-2 rounded-[14px] border border-white/10 bg-[#161616]/95 py-2 pl-2 pr-2 shadow-xl"
      data-testid={`mock-pill-${card.id}`}
    >
      {card.computer && card.state === "working" ? (
        <MockLiveLogo card={card} />
      ) : (
        <span
          aria-hidden="true"
          className={cn(
            "grid h-8 w-8 shrink-0 place-items-center rounded-[9px] text-sm font-bold",
            card.kind === "computer" ? "bg-[#1DB954] text-black" : "bg-white/10 text-white",
          )}
        >
          {card.iconLabel}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span
            className={cn(
              "h-1.5 w-1.5 shrink-0 rounded-full",
              card.state === "working" && "animate-pulse bg-sky-400",
              card.state === "question" && "bg-amber-400",
              card.state === "done" && "bg-emerald-400",
            )}
          />
          <span className="truncate text-[11px] font-semibold text-white/90">{card.title}</span>
        </span>
        <span className="block truncate text-[10px] text-white/45" data-testid={`mock-pill-progress-${card.id}`}>
          {card.progress}
        </span>
      </span>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onDismiss}
        data-testid={`mock-pill-dismiss-${card.id}`}
        className={cn(
          "shrink-0 rounded-full p-1 text-white/40 hover:bg-white/10 hover:text-white",
          card.state === "done" ? "opacity-100" : "opacity-0 group-hover/pill:opacity-100",
        )}
      >
        <Icons.X className="h-3 w-3" />
      </button>
    </div>
  );
}

export function FloatingToastCardsPage() {
  const [cards, setCards] = useState<MockCard[]>(INITIAL_CARDS);
  const [revealed, setRevealed] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);
  const timers = useRef<number[]>([]);

  useEffect(() => () => timers.current.forEach((timer) => window.clearTimeout(timer)), []);

  const dismiss = (id: string) => setCards((next) => next.filter((card) => card.id !== id));
  const setState = (id: string, state: CardState) =>
    setCards((next) => next.map((card) => (card.id === id ? { ...card, state } : card)));
  const reset = () => {
    timers.current.forEach((timer) => window.clearTimeout(timer));
    timers.current = [];
    setCards(INITIAL_CARDS);
    setNotice(null);
  };

  /** Simulate a task lifecycle: pill flashes on begin, hides while running,
   *  flashes on done, then disappears — the old toast behavior. */
  const simulateTask = () => {
    const id = `task-${Date.now()}`;
    setCards((next) => [
      ...next,
      { id, kind: "computer", title: "Spotify", state: "working", progress: "Opening track…", iconLabel: "S" },
    ]);
    setNotice("Task began — pill visible, then hides while running…");
    timers.current.push(
      window.setTimeout(() => {
        setNotice("Running silently — pill hidden. Move cursor to the edge to reveal.");
      }, 3000),
      window.setTimeout(() => {
        setCards((next) =>
          next.map((card) =>
            card.id === id ? { ...card, state: "done", progress: "Complicated is on" } : card,
          ),
        );
        setNotice("Task done — pill flashes, then disappears.");
      }, 6000),
      window.setTimeout(() => {
        setCards((next) => next.filter((card) => card.id !== id));
        setNotice(null);
      }, 12000),
    );
  };

  return (
    <div className="flex min-h-[640px] gap-6 bg-black p-6 text-white">
      {/* Controls */}
      <div className="flex w-[220px] shrink-0 flex-col gap-4">
        <h1 className="text-sm font-semibold text-white/90">Minimal pills + edge reveal</h1>
        <p className="text-[11px] leading-4 text-white/40">
          Mock for the right-edge toasts. Minimal pill: app icon + agent name + one live
          progress line. Cursor at the edge reveals; away collapses to badges.
          The research card demos merged computer-use: one sub-agent card whose
          logo cycles click → screenshot → app while working.
        </p>
        <button
          type="button"
          onClick={() => setRevealed((value) => !value)}
          data-testid="mock-toast-reveal-toggle"
          className="rounded-[10px] border border-white/15 bg-white/5 px-3 py-2 text-left text-[12px] text-white/80 hover:border-white/30"
        >
          {revealed ? "Simulate: cursor left (collapse)" : "Simulate: cursor at edge (reveal)"}
        </button>
        <button
          type="button"
          onClick={simulateTask}
          data-testid="mock-toast-simulate-task"
          className="rounded-[10px] border border-white/15 bg-white/5 px-3 py-2 text-left text-[12px] text-white/80 hover:border-white/30"
        >
          Simulate task lifecycle
        </button>
        {notice && (
          <p className="text-[11px] leading-4 text-sky-300/80" data-testid="mock-toast-notice">
            {notice}
          </p>
        )}
        {cards.map((card) => (
          <div key={card.id} className="flex flex-col gap-1.5 rounded-[10px] border border-white/10 p-2">
            <span className="truncate text-[11px] font-medium text-white/70">{card.title}</span>
            <div className="flex gap-1">
              {(["working", "question", "done"] as CardState[]).map((state) => (
                <button
                  key={state}
                  type="button"
                  onClick={() => setState(card.id, state)}
                  data-testid={`mock-toast-state-${card.id}-${state}`}
                  className={cn(
                    "flex-1 rounded-[6px] px-1 py-1 text-[10px]",
                    card.state === state ? "bg-white/20 text-white" : "bg-white/5 text-white/40 hover:text-white/70",
                  )}
                >
                  {state}
                </button>
              ))}
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={reset}
          data-testid="mock-toast-reset"
          className="text-left text-[11px] text-white/40 hover:text-white/70"
        >
          Reset cards
        </button>
      </div>

      {/* Mock desktop with the right-edge rail. */}
      <div className="relative min-h-[560px] flex-1 overflow-hidden rounded-[16px] border border-white/10 bg-[#0a0a0a]">
        <div
          className="absolute bottom-6 right-3 top-6 flex w-[280px] flex-col items-end gap-2 overflow-y-auto pb-4"
          data-testid="mock-toast-rail"
        >
          {revealed ? (
            <>
              {cards.map((card) => (
                <MockPill key={card.id} card={card} onDismiss={() => dismiss(card.id)} />
              ))}
              {cards.some((card) => card.state === "question") && (
                <div
                  className="flex w-[264px] flex-col gap-1 rounded-[14px] border border-amber-300/20 bg-[#161616]/95 p-2"
                  data-testid="mock-toast-question-box"
                >
                  <p className="text-[11px] text-white/85">Archive the 3 promo mails?</p>
                  {QUESTION_OPTIONS.map((option, index) => (
                    <button
                      key={option}
                      type="button"
                      data-testid={`mock-toast-option-${index}`}
                      className="flex items-center gap-2 rounded-[8px] border border-white/10 bg-white/5 px-2 py-1.5 text-left text-[11px] text-white/85 hover:border-white/25"
                    >
                      <kbd className="grid h-4 w-4 shrink-0 place-items-center rounded border border-white/15 text-[9px] text-white/40">
                        {index + 1}
                      </kbd>
                      {option}
                    </button>
                  ))}
                  <div className="flex items-center gap-1.5 pt-1">
                    <span className="min-w-0 flex-1 truncate text-[10px] text-white/45">
                      Latest: promo digest from 3 senders
                    </span>
                    <button
                      type="button"
                      aria-label="Dictate"
                      data-testid="mock-toast-mic"
                      className="shrink-0 rounded-full p-1.5 text-white/50 hover:bg-white/10 hover:text-white"
                    >
                      <Icons.Mic className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      aria-label="Type"
                      data-testid="mock-toast-type"
                      className="shrink-0 rounded-full p-1.5 text-white/50 hover:bg-white/10 hover:text-white"
                    >
                      <Icons.MessageSquare className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-end gap-3 pt-2">
              {cards.map((card) => (
                <button
                  key={card.id}
                  type="button"
                  aria-label={`Reveal ${card.title}`}
                  onClick={() => setRevealed(true)}
                  data-testid={`mock-toast-badge-${card.id}`}
                  className="relative transition-transform hover:scale-105"
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      "grid h-9 w-9 place-items-center rounded-[10px] text-sm font-bold shadow-lg ring-1 ring-white/20",
                      card.kind === "computer" ? "bg-[#1DB954] text-black" : "bg-white/10 text-white",
                    )}
                  >
                    {card.iconLabel}
                  </span>
                  <span
                    className={cn(
                      "absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full ring-1 ring-black/60",
                      card.state === "working" && "animate-pulse bg-sky-400",
                      card.state === "question" && "bg-amber-400",
                      card.state === "done" && "bg-white/40",
                    )}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
